import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import SavedSearchService from '../services/SavedSearchService';
import SavedSearchStorage from '../services/SavedSearchStorage';
import { sanitizeFilter, validateFilter, isSystemFilter, removeSystemFiltersFromConfig, mergeSystemAndUserFilters } from '../utils/filterManager';
import {
  hasConfigurationChanges,
  validateSearchName
} from '../utils/savedSearchHelpers';
import { MESSAGES, VISIBILITY_TYPES } from '../utils/savedSearchConstants';
import { SavedSearch, Filter, VisibilityType, ConfigMode } from '../types';

export interface UseSavedSearchOptions {
  configMode?: ConfigMode;
  profileType?: string;
  storageKeyPrefix?: string;
  defaultProfiles?: SavedSearch[];
}

const MESSAGE_TIMEOUT_SUCCESS = 3000;
const MESSAGE_TIMEOUT_ERROR = 5000;

/** Find a search in a list by id, falling back to case-insensitive name match. */
function findSearch(searches: SavedSearch[], target: Partial<SavedSearch>) {
  return searches.find(s => String(s.id) === String(target.id))
    || searches.find(s => (s.name || '').toLowerCase() === (target.name || '').toLowerCase());
}

export function useSavedSearch(
  tableType: string,
  initialConfig: Record<string, any> = {},
  onInitialLoad: ((config: Record<string, any>, isInitial: boolean) => void) | null = null,
  initialFilters: Filter | { logic: string; filters: Filter[] } | null = null,
  options: UseSavedSearchOptions = {}
) {
  const {
    configMode = 'table',
    profileType = 'SavedSearch',
    storageKeyPrefix = 'savedSearch-',
    defaultProfiles = []
  } = options;
  const isOpaque = configMode === 'opaque';
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [currentSearch, setCurrentSearch] = useState<SavedSearch | null>(null);
  const [currentConfig, setCurrentConfig] = useState<Record<string, any>>(initialConfig);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasAppliedFromURL, setHasAppliedFromURL] = useState(false);
  const [hasLoadedSearches, setHasLoadedSearches] = useState(false);

  const abortControllerRef = useRef(null);
  const messageTimeoutsRef = useRef({ success: null, error: null });

  // --- Helpers ---

  const systemFiltersOnly = useMemo(() => {
    const sourceFilters = initialFilters || initialConfig.filters;
    if (!sourceFilters) return { logic: 'and', filters: [] };
    if (isSystemFilter(sourceFilters)) return sourceFilters;
    const allFilters = sourceFilters.filters || [];
    return { logic: sourceFilters.logic || 'and', filters: allFilters.filter(f => isSystemFilter(f)) };
  }, [initialFilters, initialConfig.filters]);

  const processSavedSearchConfig = useCallback((search) => {
    if (!search) return null;
    if (isOpaque) return search.configuration;

    let configuration = removeSystemFiltersFromConfig(search.configuration);
    if (configuration?.filters) {
      configuration = { ...configuration, filters: sanitizeFilter(configuration.filters) };
    }
    if (configuration) {
      configuration = { ...configuration, filters: mergeSystemAndUserFilters(systemFiltersOnly, configuration.filters) };
    }
    return configuration;
  }, [isOpaque, systemFiltersOnly]);

  /** Fetch searches from service and merge with synthetic defaults. */
  const fetchAndMergeSearches = useCallback(async (signal?: AbortSignal) => {
    const searches = await SavedSearchService.loadSavedSearches(
      tableType, signal || null, profileType, storageKeyPrefix, configMode
    );

    // Repair legacy entries with missing names
    for (const search of searches) {
      if (!search.name || search.name === 'Untitled') {
        const match = defaultProfiles.find(dp => String(dp.id) === String(search.id));
        if (match) search.name = match.name;
      }
    }

    // Merge synthetic defaults (only those not already loaded)
    const loadedIds = new Set(searches.map(s => String(s.id)));
    const syntheticDefaults = defaultProfiles
      .filter(dp => !loadedIds.has(String(dp.id)))
      .map(dp => ({ ...dp, isSyntheticDefault: true }));
    const allSearches = [...syntheticDefaults, ...searches];

    // Resolve stored default if the service didn't mark one
    if (!allSearches.some(s => s.isDefault)) {
      const storedDefaultId = await SavedSearchStorage.getDefaultSearchId(tableType);
      if (storedDefaultId) {
        const match = allSearches.find(s => String(s.id) === String(storedDefaultId))
          // Prefix fallback for id format migrations (e.g. old synthetic id → new)
          || allSearches.find(s =>
            (s as any).isSyntheticDefault &&
            (String(storedDefaultId).startsWith(String(s.id)) || String(s.id).startsWith(String(storedDefaultId)))
          );
        if (match) match.isDefault = true;
      }
    }

    return allSearches;
  }, [tableType, profileType, storageKeyPrefix, configMode, defaultProfiles]);

  // --- Core operations ---

  const loadSavedSearches = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);

    try {
      const allSearches = await fetchAndMergeSearches(abortController.signal);
      if (!abortController.signal.aborted) {
        setSavedSearches(allSearches);
        setHasLoadedSearches(true);
      }
    } catch (err) {
      if (!abortController.signal.aborted) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Error loading saved searches:', err);
          setError(MESSAGES.LOAD_ERROR);
          setHasLoadedSearches(true);
        }
      }
    } finally {
      if (!abortController.signal.aborted) setIsLoading(false);
      if (abortControllerRef.current === abortController) abortControllerRef.current = null;
    }
  }, [fetchAndMergeSearches]);

  const applySavedSearch = useCallback((searchId) => {
    const search = savedSearches.find(s => String(s.id) === String(searchId));
    if (!search) { setError('Saved search not found'); return false; }

    const configuration = processSavedSearchConfig(search);
    setCurrentSearch(search);
    setCurrentConfig(configuration);
    if (!isOpaque) SavedSearchStorage.updateURLWithSearchId(searchId);
    return configuration;
  }, [savedSearches, processSavedSearchConfig, isOpaque]);

  const saveCurrentConfig = useCallback(async (name = null, shareType = 'private', configToSave = null) => {
    setIsLoading(true);
    setError(null);

    let configToUse = configToSave || currentConfig;

    // Table mode: strip system filters and sanitize
    if (!isOpaque && configToUse?.filters?.filters) {
      const nonSystemFilters = configToUse.filters.filters.filter(f => !isSystemFilter(f));
      configToUse = { ...configToUse, filters: { ...configToUse.filters, filters: nonSystemFilters } };
    }
    if (!isOpaque && configToUse?.filters) {
      configToUse = { ...configToUse, filters: sanitizeFilter(configToUse.filters) };
      if (!validateFilter(configToUse.filters)) {
        configToUse.filters = { logic: 'and', filters: [] };
      }
    }

    try {
      const isSyntheticDefault = (currentSearch as any)?.isSyntheticDefault === true;
      const isUpdate = currentSearch && !name && !isSyntheticDefault;
      const isSavingOverDefault = isSyntheticDefault && !name;

      let savedSearch: SavedSearch;

      if (isUpdate) {
        // Update existing persisted search
        savedSearch = await SavedSearchService.updateSavedSearch(
          currentSearch.id,
          { configuration: configToUse, tableType },
          profileType, storageKeyPrefix, configMode,
          currentSearch
        );
      } else {
        // Create new search (or persist a synthetic default)
        const searchName = name || (isSyntheticDefault ? currentSearch.name : `Search ${new Date().toLocaleDateString()}`);

        // Validate name — when saving over a default, exclude the default itself from duplicate check
        const searchesForValidation = isSavingOverDefault
          ? savedSearches.filter(s =>
              !defaultProfiles.some(dp => String(dp.id) === String(s.id)) &&
              (s.name || '').toLowerCase() !== searchName.toLowerCase()
            )
          : savedSearches;
        const nameError = validateSearchName(searchName, searchesForValidation);
        if (nameError) { setError(nameError); return null; }

        savedSearch = await SavedSearchService.saveSavedSearch(
          {
            name: searchName,
            tableType,
            configuration: configToUse,
            visibility: shareType as VisibilityType,
            ...(isSavingOverDefault && currentSearch ? { id: currentSearch.id } : {})
          },
          profileType, storageKeyPrefix, configMode
        );
      }

      // Reload and select the saved entry
      const updatedSearches = await fetchAndMergeSearches();
      const selected = findSearch(updatedSearches, savedSearch);
      if (selected) {
        setSavedSearches(updatedSearches);
        setCurrentSearch(selected);
        if (!isOpaque) SavedSearchStorage.updateURLWithSearchId(selected.id);
      } else {
        // Fallback: reload didn't find the entry (e.g. shared save with placeholder id
        // and the API hasn't propagated yet). Append locally and select it.
        setSavedSearches([...updatedSearches, savedSearch]);
        setCurrentSearch(savedSearch);
        if (!isOpaque) SavedSearchStorage.updateURLWithSearchId(savedSearch.id);
      }

      setSuccessMessage(MESSAGES.SAVE_SUCCESS);
      return savedSearch;
    } catch (err) {
      console.error('Error saving search configuration:', err);
      setError(MESSAGES.SAVE_ERROR);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [currentSearch, currentConfig, tableType, savedSearches, isOpaque, profileType, storageKeyPrefix, configMode, defaultProfiles, fetchAndMergeSearches]);

  const deleteSavedSearch = useCallback(async (searchId = null) => {
    const idToDelete = searchId || currentSearch?.id;
    if (!idToDelete) { setError('No search to delete'); return false; }

    setIsLoading(true);
    setError(null);

    try {
      const searchToDelete = savedSearches.find(s => String(s.id) === String(idToDelete));
      await SavedSearchService.deleteSavedSearch(
        idToDelete, tableType, profileType, storageKeyPrefix, configMode,
        searchToDelete?.visibility || VISIBILITY_TYPES.PRIVATE,
        searchToDelete?.isDefault || false
      );

      // Immediately remove from list
      setSavedSearches(prev => prev.filter(s => String(s.id) !== String(idToDelete)));

      // Clear current if deleting the active search
      let clearedConfig = null;
      if (String(currentSearch?.id) === String(idToDelete)) {
        setCurrentSearch(null);
        if (isOpaque) {
          setCurrentConfig({});
        } else {
          SavedSearchStorage.updateURLWithSearchId(null);
          clearedConfig = { ...currentConfig, filters: systemFiltersOnly, searchTerm: '' };
          setCurrentConfig(clearedConfig);
        }
      }

      await loadSavedSearches();
      setSuccessMessage(MESSAGES.DELETE_SUCCESS);
      return isOpaque ? null : (clearedConfig || true);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('Error deleting saved search:', err);
        setError(MESSAGES.DELETE_ERROR);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentSearch, currentConfig, systemFiltersOnly, tableType, loadSavedSearches, isOpaque, profileType, storageKeyPrefix, configMode, savedSearches]);

  const setAsDefault = useCallback(async (searchId = null) => {
    const idToSet = searchId || currentSearch?.id;
    if (!idToSet) { setError('No search to set as default'); return false; }

    setIsLoading(true);
    setError(null);
    try {
      await SavedSearchService.setDefaultSearch(idToSet, tableType);
      setSavedSearches(prev => prev.map(s => ({ ...s, isDefault: s.id === idToSet })));
      if (currentSearch?.id === idToSet) setCurrentSearch(prev => ({ ...prev, isDefault: true }));
      setSuccessMessage('Search set as default');
      return true;
    } catch (err) {
      console.error('Error setting default search:', err);
      setError('Failed to set default search');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentSearch, tableType]);

  const removeAsDefault = useCallback(async (searchId = null) => {
    const idToRemove = searchId || currentSearch?.id;
    if (!idToRemove) { setError('No search to remove as default'); return false; }

    setIsLoading(true);
    setError(null);
    try {
      await SavedSearchService.setDefaultSearch(null, tableType);
      setSavedSearches(prev => prev.map(s => ({ ...s, isDefault: false })));
      if (currentSearch?.id === idToRemove) setCurrentSearch(prev => ({ ...prev, isDefault: false }));
      setSuccessMessage('Default search removed');
      return true;
    } catch (err) {
      console.error('Error removing default search:', err);
      setError('Failed to remove default search');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentSearch, tableType]);

  const clearAllFilters = useCallback(() => {
    setCurrentSearch(null);
    if (isOpaque) {
      setCurrentConfig({});
      return null;
    }
    SavedSearchStorage.updateURLWithSearchId(null);
    const clearedConfig = { ...currentConfig, filters: systemFiltersOnly, searchTerm: '' };
    setCurrentConfig(clearedConfig);
    return clearedConfig;
  }, [currentConfig, systemFiltersOnly, isOpaque]);

  const shareSavedSearch = useCallback(async (searchId, shareType) => {
    setIsLoading(true);
    setError(null);
    try {
      const searchToShare = savedSearches.find(s => String(s.id) === String(searchId));
      const shared = await SavedSearchService.shareSavedSearch(
        searchId, shareType, tableType, profileType, storageKeyPrefix, configMode,
        searchToShare
      );
      const updatedSearches = await fetchAndMergeSearches();
      const selected = findSearch(updatedSearches, shared);
      const finalEntry = selected || shared;
      setSavedSearches(selected ? updatedSearches : [...updatedSearches, shared]);
      if (currentSearch?.id === searchId) {
        setCurrentSearch(finalEntry);
        // Update URL to the new id (sharing can change ids, e.g. private → API)
        if (!isOpaque) SavedSearchStorage.updateURLWithSearchId(finalEntry.id);
      }
      return shared;
    } catch (err) {
      console.error('Error sharing saved search:', err);
      setError('Failed to share search');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [currentSearch, savedSearches, tableType, fetchAndMergeSearches, profileType, storageKeyPrefix, configMode, isOpaque]);

  const renameSavedSearch = useCallback(async (searchId, newName) => {
    setIsLoading(true);
    setError(null);
    try {
      const searchToRename = savedSearches.find(s => String(s.id) === String(searchId));
      const updated = await SavedSearchService.updateSavedSearch(
        searchId, { name: newName, tableType }, profileType, storageKeyPrefix, configMode,
        searchToRename
      );
      const updatedSearches = await fetchAndMergeSearches();
      const selected = findSearch(updatedSearches, updated);
      if (selected) {
        setSavedSearches(updatedSearches);
        if (currentSearch?.id === searchId) setCurrentSearch(selected);
      } else {
        setSavedSearches([...updatedSearches, updated]);
        if (currentSearch?.id === searchId) setCurrentSearch(updated);
      }
      setSuccessMessage('Saved search renamed successfully');
      return updated;
    } catch (err) {
      console.error('Error renaming saved search:', err);
      setError('Failed to rename search');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [currentSearch, savedSearches, tableType, fetchAndMergeSearches, profileType, storageKeyPrefix, configMode]);

  // --- Config tracking ---

  const debouncedSetConfig = useMemo(
    () => debounce((newConfig) => {
      setCurrentConfig(prevConfig => {
        if (JSON.stringify(prevConfig) !== JSON.stringify(newConfig)) return newConfig;
        return prevConfig;
      });
    }, 300),
    []
  );

  useEffect(() => () => { debouncedSetConfig.cancel(); }, [debouncedSetConfig]);

  const updateCurrentConfig = useCallback((newConfig) => {
    debouncedSetConfig(newConfig);
  }, [debouncedSetConfig]);

  const hasUnsavedChanges = useMemo(() => {
    if (!currentSearch) return false;
    return hasConfigurationChanges(currentConfig, currentSearch.configuration);
  }, [currentConfig, currentSearch]);

  const filterCount = useMemo(() => {
    if (!currentConfig?.filters?.filters) return 0;
    if (isSystemFilter(currentConfig.filters)) return 0;
    const countNonSystem = (filters) => {
      let count = 0;
      for (const f of filters) {
        if (isSystemFilter(f)) continue;
        if (f.filters && Array.isArray(f.filters)) count += countNonSystem(f.filters);
        else if (f.field) count++;
      }
      return count;
    };
    return countNonSystem(currentConfig.filters.filters);
  }, [currentConfig]);

  // --- Effects ---

  // Mount: load searches
  useEffect(() => {
    setHasAppliedFromURL(false);
    setHasLoadedSearches(false);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    loadSavedSearches();

    const timeouts = messageTimeoutsRef.current;
    return () => {
      if (abortControllerRef.current) { abortControllerRef.current.abort(); abortControllerRef.current = null; }
      if (timeouts.success) { clearTimeout(timeouts.success); timeouts.success = null; }
      if (timeouts.error) { clearTimeout(timeouts.error); timeouts.error = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableType]);

  // After load: apply default or URL-specified search
  useEffect(() => {
    if (!hasLoadedSearches || hasAppliedFromURL) return;

    if (isOpaque) {
      // Opaque mode: apply default search (no URL handling)
      const defaultSearch = savedSearches.find(s => s.isDefault);
      if (defaultSearch) {
        const configuration = processSavedSearchConfig(defaultSearch);
        if (configuration) {
          setCurrentSearch(defaultSearch);
          setCurrentConfig(configuration);
          if (onInitialLoad) onInitialLoad(configuration, true);
        }
      }
      setHasAppliedFromURL(true);
      return;
    }

    // Table mode: check URL, then fall back to default
    const searchFromURL = SavedSearchStorage.getSearchIdFromURL();
    let searchToApply = null;
    let configuration = null;

    if (searchFromURL) {
      const search = savedSearches.find(s => String(s.id) === String(searchFromURL));
      if (search) {
        searchToApply = search;
        configuration = processSavedSearchConfig(search);
      } else {
        SavedSearchStorage.updateURLWithSearchId(null);
        const defaultSearch = savedSearches.find(s => s.isDefault);
        if (defaultSearch) { searchToApply = defaultSearch; configuration = processSavedSearchConfig(defaultSearch); }
      }
    } else if (!SavedSearchStorage.hasManualFilters()) {
      const defaultSearch = savedSearches.find(s => s.isDefault);
      if (defaultSearch) { searchToApply = defaultSearch; configuration = processSavedSearchConfig(defaultSearch); }
    }

    if (searchToApply && configuration) {
      setCurrentSearch(searchToApply);
      setCurrentConfig(configuration);
      const urlHasFilters = window.location.search.includes('q.0') || window.location.search.includes('q.1');
      if (!urlHasFilters && onInitialLoad) onInitialLoad(configuration, true);
      if (searchToApply.id !== searchFromURL) SavedSearchStorage.updateURLWithSearchId(searchToApply.id, urlHasFilters);
    } else if (!searchToApply) {
      setCurrentSearch(null);
      setCurrentConfig(prev => ({ ...prev, filters: systemFiltersOnly }));
    }

    setHasAppliedFromURL(true);
  }, [savedSearches, hasLoadedSearches, hasAppliedFromURL, onInitialLoad, systemFiltersOnly, processSavedSearchConfig, isOpaque]);

  // Table mode: watch for URL changes
  useEffect(() => {
    if (isOpaque || !hasAppliedFromURL) return;

    const currentSearchId = SavedSearchStorage.getSearchIdFromURL();
    const activeId = currentSearch?.id ?? null;

    if (currentSearchId !== activeId) {
      if (currentSearchId) {
        const search = savedSearches.find(s => String(s.id) === String(currentSearchId));
        if (search) {
          setCurrentSearch(search);
          setCurrentConfig(processSavedSearchConfig(search));
        } else {
          SavedSearchStorage.updateURLWithSearchId(null);
          setCurrentSearch(null);
          setCurrentConfig(prev => ({ ...prev, filters: systemFiltersOnly }));
        }
      } else {
        setCurrentSearch(null);
        setCurrentConfig(prev => ({ ...prev, filters: systemFiltersOnly }));
      }
    }
  }, [hasAppliedFromURL, currentSearch, savedSearches, systemFiltersOnly, processSavedSearchConfig, isOpaque]);

  // Auto-dismiss messages
  useEffect(() => {
    if (successMessage) {
      if (messageTimeoutsRef.current.success) clearTimeout(messageTimeoutsRef.current.success);
      const id = setTimeout(() => { setSuccessMessage(null); messageTimeoutsRef.current.success = null; }, MESSAGE_TIMEOUT_SUCCESS);
      messageTimeoutsRef.current.success = id;
      return () => clearTimeout(id);
    }
    if (error) {
      if (messageTimeoutsRef.current.error) clearTimeout(messageTimeoutsRef.current.error);
      const id = setTimeout(() => { setError(null); messageTimeoutsRef.current.error = null; }, MESSAGE_TIMEOUT_ERROR);
      messageTimeoutsRef.current.error = id;
      return () => clearTimeout(id);
    }
  }, [successMessage, error]);

  return {
    savedSearches, currentSearch, currentConfig, isLoading, error, successMessage,
    hasUnsavedChanges, filterCount,
    applySavedSearch, saveCurrentConfig, deleteSavedSearch, setAsDefault, removeAsDefault,
    clearAllFilters, updateCurrentConfig, shareSavedSearch, renameSavedSearch, loadSavedSearches
  };
}
